import { useCallback, useEffect, useMemo, useState } from "react";
import {
	addChatImagePlaceholders,
	listChatImagePlaceholders,
	removeChatImagePlaceholders,
} from "@/lib/api";
import { placeholderKey, type ImagePlaceholderTarget } from "@/lib/chat-files";

export interface ChatImagePlaceholders {
	/** 置き換え済みの位置キー (placeholderKey)。取得完了まで null。 */
	replacedKeys: ReadonlySet<string> | null;
	/** 取得に失敗したときのメッセージ。 */
	loadError: string | null;
	/** 画像をモデル入力から外す。失敗はそのまま throw する。 */
	replace: (targets: readonly ImagePlaceholderTarget[]) => Promise<void>;
	/** 置き換えを取り消す。失敗はそのまま throw する。 */
	restore: (targets: readonly ImagePlaceholderTarget[]) => Promise<void>;
}

function toKeySet(targets: readonly ImagePlaceholderTarget[]): ReadonlySet<string> {
	return new Set(targets.map(placeholderKey));
}

/**
 * チャットの画像プレースホルダー状態。マウント時にサーバから読み、追加・取り消しの応答 (更新後の一覧) で
 * 差し替える。履歴 (messages) は書き換えない設計なので、状態はこのフックの中だけで持つ。
 */
export function useChatImagePlaceholders(chatId: string): ChatImagePlaceholders {
	const [targets, setTargets] = useState<readonly ImagePlaceholderTarget[] | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		setTargets(null);
		setLoadError(null);
		listChatImagePlaceholders(chatId).then(
			(loaded) => {
				if (!cancelled) setTargets(loaded);
			},
			(err: unknown) => {
				if (cancelled) return;
				setLoadError(
					err instanceof Error ? err.message : "画像の置き換え状態の取得に失敗しました。",
				);
				console.error("画像の置き換え状態の取得に失敗しました", err);
			},
		);
		return () => {
			cancelled = true;
		};
	}, [chatId]);

	const replace = useCallback(
		async (toReplace: readonly ImagePlaceholderTarget[]) => {
			setTargets(await addChatImagePlaceholders(chatId, toReplace));
		},
		[chatId],
	);
	const restore = useCallback(
		async (toRestore: readonly ImagePlaceholderTarget[]) => {
			setTargets(await removeChatImagePlaceholders(chatId, toRestore));
		},
		[chatId],
	);
	const replacedKeys = useMemo(() => (targets === null ? null : toKeySet(targets)), [targets]);
	return { replacedKeys, loadError, replace, restore };
}
